/**
 * Attribute Domain Models and DTOs
 * Based on Attributes API documentation
 */

// ==============================|| ENUMS ||============================== //

export enum AttributeType {
  Text = 0,
  Number = 1,
  Boolean = 2,
  Date = 3,
  MultiSelect = 4,
  Color = 5,
  Range = 6,
  Image = 7,
  File = 8
}

export const ATTRIBUTE_TYPE_VALUES: AttributeType[] = [
  AttributeType.Text,
  AttributeType.Number,
  AttributeType.Boolean,
  AttributeType.Date,
  AttributeType.MultiSelect,
  AttributeType.Color,
  AttributeType.Range,
  AttributeType.Image,
  AttributeType.File
];

export const ATTRIBUTE_TYPE_I18N_KEYS: Record<AttributeType, string> = {
  [AttributeType.Text]: 'Text',
  [AttributeType.Number]: 'Number',
  [AttributeType.Boolean]: 'Boolean',
  [AttributeType.Date]: 'Date',
  [AttributeType.MultiSelect]: 'MultiSelect',
  [AttributeType.Color]: 'Color',
  [AttributeType.Range]: 'Range',
  [AttributeType.Image]: 'Image',
  [AttributeType.File]: 'File'
};

const ATTRIBUTE_TYPE_BY_NAME: Record<string, AttributeType> = Object.fromEntries(
  Object.entries(ATTRIBUTE_TYPE_I18N_KEYS).map(([type, name]) => [name.toLowerCase(), Number(type) as AttributeType])
) as Record<string, AttributeType>;

/** Normalize API attribute type (number, numeric string, or enum name) to AttributeType */
export function normalizeAttributeType(type: unknown): AttributeType {
  if (typeof type === 'number' && type in ATTRIBUTE_TYPE_I18N_KEYS) {
    return type as AttributeType;
  }

  if (typeof type === 'string') {
    const numericType = Number(type);
    if (!Number.isNaN(numericType) && numericType in ATTRIBUTE_TYPE_I18N_KEYS) {
      return numericType as AttributeType;
    }

    const byName = ATTRIBUTE_TYPE_BY_NAME[type.toLowerCase()];
    if (byName !== undefined) {
      return byName;
    }
  }

  return AttributeType.Text;
}

export function normalizeAttributeResult(attribute: AttributeResult): AttributeResult {
  return {
    ...attribute,
    type: normalizeAttributeType(attribute.type)
  };
}

// ==============================|| DTOs ||============================== //

export interface AttributeResult {
  id: string;
  name: string;
  slug: string;
  displayName: string;
  type: AttributeType;
  categoryId: string;
  isRequired: boolean;
  isFilterable: boolean;
  displayOrder: number;
  unit: string | null;
  options: AttributeOptionResult[];
}

export interface AttributeOptionResult {
  id: string;
  value: string;
  displayValue: string;
}

export interface CreateAttributeRequest {
  name: string;
  slug: string;
  type: AttributeType;
  categoryId: string;
  isFilterable: boolean;
  isRequired: boolean;
  displayOrder: number;
  unit?: string | null;
  options?: string[];
}

export interface UpdateAttributeRequest {
  name: string;
  slug: string;
  type: AttributeType;
  isFilterable: boolean;
  isRequired: boolean;
  displayOrder: number;
  unit?: string | null;
  options?: UpdateAttributeOption[];
}

export interface UpdateAttributeOption {
  id: string | null;
  value: string;
  displayValue: string;
}

export interface AttributeDeleteResponse {
  message: string;
}
